/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { ProfileSettings } from 'src/components/Profile';
import Placeholder from 'src/components/Profile/ProfileSettings/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getEmptyRetryVariables from './helpers/getEmptyRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'profile.profile_settings.initial_load_error.message',
    defaultMessage: 'An error occurred while loading your profile information. Please try again.',
  },
});

export const profileSettingsRouteQuery = graphql`
  query ProfileSettingsRoute_Query {
    viewer {
      ...ProfileSettings_viewer
    }
  }
`;

const profileSettingsRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({}),
});

/**
 * Renders `ProfileSettings` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} profile settings route content.
 */
function ProfileSettingsPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(profileSettingsRouteQuery, queryRef);
  return <ProfileSettings {...routeProps} {...queryData} />;
}

ProfileSettingsPreloadedQueryRenderer.propTypes = {
  routeProps: profileSettingsRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `ProfileSettings` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} profile settings route content.
 */
function ProfileSettingsRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    profileSettingsRouteQuery,
    routeProps,
    { logLabel: 'ProfileSettingsRoute', getRetryVariablesFunc: getEmptyRetryVariables },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={Placeholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <ProfileSettingsPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <ProfileSettings {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

ProfileSettingsRouteRenderer.propTypes = {
  routeProps: profileSettingsRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the ProfileSettings route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. Authentication is resolved by the
 * owning `CustomerLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} ProfileSettings route content or loading state.
 */
export function profileSettingsRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <ProfileSettingsRouteRenderer
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return <ProfileSettingsRouteRenderer routeProps={props} />;
}

export default ProfileSettingsRouteRenderer;
